import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { SimpleModal, BackdropLoader } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { AddPricingModalContent } from './content';
function parseNum(val) {
  if (val == null || val === '') return 0;
  const s = String(val).replace(/^\$?\s*/, '');
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

/** API: omit min as null when 0, empty, or invalid. */
function minimumPriceForApi(raw) {
  const n = parseNum(raw);
  return n > 0 ? n : null;
}
function buildCreatePriceBody(payload) {
  var _Array$isArray$, _Array$isArray;
  const isTiered = payload.type === 'Tiered';
  const minimumPrice = minimumPriceForApi(payload.minimumPrice);
  return {
    name: payload.name || '',
    subscriptionId: (_Array$isArray$ = (_Array$isArray = Array.isArray(payload.subscriptionIds)) === null || _Array$isArray === void 0 ? void 0 : _Array$isArray[0]) !== null && _Array$isArray$ !== void 0 ? _Array$isArray$ : '',
    productId: payload.productId || '',
    type: payload.type || 'Fixed',
    fixedPrice: isTiered ? null : parseNum(payload.fixedPrice),
    minimumPrice,
    tierTableRequest: isTiered ? {
      tierPrice: (payload.tiers || []).map(t => ({
        unitQuantity: parseInt(String(t.unitsQuantity), 10) || 0,
        amount: parseNum(t.pricePerUnit)
      }))
    } : null,
    meterEvent: payload.meterEvent || undefined
  };
}
function buildUpdatePriceBody(payload, pricing) {
  const isTiered = payload.type === 'Tiered';
  const minimumPrice = minimumPriceForApi(payload.minimumPrice);
  return {
    id: (pricing === null || pricing === void 0 ? void 0 : pricing.priceId) || (pricing === null || pricing === void 0 ? void 0 : pricing.id) || '',
    priceName: payload.name || '',
    productId: payload.productId || '',
    priceType: payload.type || 'Fixed',
    fixedPrice: isTiered ? null : parseNum(payload.fixedPrice),
    minimumPrice,
    tierTableRequest: isTiered ? {
      tierPrice: (payload.tiers || []).map(t => ({
        unitQuantity: parseInt(String(t.unitsQuantity), 10) || 0,
        amount: parseNum(t.pricePerUnit)
      }))
    } : null,
    meterEvent: payload.meterEvent || undefined
  };
}
export const AddPricingModal = observer(function AddPricingModal(_ref) {
  var _store$stripe$createP, _store$stripe$createP2, _store$stripe$updateP, _store$stripe$updateP2;
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    mode = _ref.mode,
    pricing = _ref.pricing,
    _ref$defaultProductId = _ref.defaultProductId,
    defaultProductId = _ref$defaultProductId === void 0 ? '' : _ref$defaultProductId,
    onSave = _ref.onSave;
  const store = useStore();
  const contentRef = useRef(null);
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    submitError = _useState2[0],
    setSubmitError = _useState2[1];
  const isEdit = mode === 'edit';
  const isSubmitting = ((_store$stripe$createP = (_store$stripe$createP2 = store.stripe.createPriceAsync) === null || _store$stripe$createP2 === void 0 ? void 0 : _store$stripe$createP2.inProgress) !== null && _store$stripe$createP !== void 0 ? _store$stripe$createP : false) || ((_store$stripe$updateP = (_store$stripe$updateP2 = store.stripe.updatePriceAsync) === null || _store$stripe$updateP2 === void 0 ? void 0 : _store$stripe$updateP2.inProgress) !== null && _store$stripe$updateP !== void 0 ? _store$stripe$updateP : false);
  useEffect(() => {
    if (isOpen) setSubmitError(null);
  }, [isOpen]);
  const handleSubmit = async () => {
    var _contentRef$current;
    if (!((_contentRef$current = contentRef.current) !== null && _contentRef$current !== void 0 && _contentRef$current.validate())) return;
    setSubmitError(null);
    const payload = contentRef.current.getPayload();
    try {
      if (isEdit) {
        const body = buildUpdatePriceBody(payload, pricing);
        if (!body.id) {
          setSubmitError('Price id is missing');
          return;
        }
        await store.stripe.updatePriceAsync.run(body);
      } else {
        const body = buildCreatePriceBody(payload);
        await store.stripe.createPriceAsync.run(body);
      }
      onSave === null || onSave === void 0 || onSave();
      onClose();
      await store.stripe.getPriceDashboardAsync.run();
      await store.stripe.getPricesAsync.run();
    } catch (err) {
      var _err$message;
      console.error(isEdit ? 'Update price failed:' : 'Create price failed:', err);
      setSubmitError((_err$message = err === null || err === void 0 ? void 0 : err.message) !== null && _err$message !== void 0 ? _err$message : isEdit ? 'Failed to update pricing' : 'Failed to save pricing');
    }
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: null,
    isOpen: isOpen,
    handleClose: onClose,
    title: isEdit ? 'Edit pricing' : 'Add pricing',
    leftButton: /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      onClick: onClose,
      disabled: isSubmitting
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleSubmit,
      disabled: isSubmitting
    }, isSubmitting ? 'Saving...' : 'Save pricing')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isSubmitting
  }), /*#__PURE__*/React.createElement(AddPricingModalContent, {
    ref: contentRef,
    isOpen: isOpen,
    isEdit: isEdit,
    pricing: pricing,
    defaultProductId: defaultProductId,
    submitError: submitError
  }));
});